import {Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef} from '@angular/core';
import {MasterService} from '../../../shared/services/master.service';
import {UsersService} from '../../../shared/services/users.service';
import {ActivatedRoute, Router} from '@angular/router';
import {
    DiarySummarySingleClientViewModel,
    DiarySummaryViewModel,
    MasterRecipeViewModel
} from '../../../shared/viewModels/diarySummary.viewModel';
import * as moment from 'moment';
import {AppLoadingEventsEmitter} from '../../../shared/events/loading.events';
import {ViewRecipeComponent} from '../../../meals/components/modals/view-recipe/view-recipe.component';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {BsModalEventsEmitter} from '../../../shared/events/bsModal.events';
import {ViewRecipeStatisticsComponent} from './view-recipe/view-recipe-statistics.component';

@Component({
    selector: 'app-master-statistics',
    templateUrl: './statistics.component.html',
    styleUrls: ['./statistics.component.scss']
})
export class StatisticsComponent implements OnInit {

    protected readonly Object = Object;

    diarySummary: DiarySummaryViewModel;
    fromDate: Date;
    toDate: Date;
    settings = {
        bigBanner: false,
        timePicker: false,
        format: 'dd.MM.yyyy',
        defaultOpen: false,
        closeOnSelect: true
    };

    selectedClientIds: number[] = [];
    clients: DiarySummarySingleClientViewModel[] = [];
    viewRecipeBsModalRef: BsModalRef;

    constructor(
        private readonly masterService: MasterService,
        private readonly usersService: UsersService,
        private readonly router: Router,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly appLoadingEventsEmitter: AppLoadingEventsEmitter,
        private readonly bsModalService: BsModalService,
        private readonly bsModalEventsEmitter: BsModalEventsEmitter,
        public readonly bsModalRef: BsModalRef,
    ) {
    }

    ngOnInit() {
        const isMasterProfile = (this.usersService.userProfile && this.usersService.userProfile.is_master_profile === true);

        if (!isMasterProfile) {
            this.router.navigate(['/']);
        }

        this.resetDates();
        this.init();
    }

    init() {
        if ((moment(this.toDate).diff(moment(this.fromDate), 'days') + 1) > 14) {
            alert('Изберете период по-малък или равен на 14 дни.');
            return;
        }

        this.appLoadingEventsEmitter.broadcastAppIsLoading(true);
        this.diarySummary = null;

        this.masterService.getDiarySummaryClients(
            this.usersService.userProfile.id,
            moment(this.fromDate).format('YYYY-MM-DD'),
            moment(this.toDate).format('YYYY-MM-DD')
        ).subscribe(data => {
            this.clients = data.allClients;

            if (data.selectedClientIds.length <= 0) {
                this.selectedClientIds = data.allClients.map(value => value.id);
            } else {
                this.selectedClientIds = data.selectedClientIds;
            }

            this.initStatistics();
        });
    }

    initStatistics() {
        this.masterService
            .getDiarySummary(
                this.usersService.userProfile.id,
                moment(this.fromDate).format('YYYY-MM-DD'),
                moment(this.toDate).format('YYYY-MM-DD'),
                this.selectedClientIds
            )
            .subscribe((data) => {
                this.diarySummary = data;

                this.diarySummary.groupedProductsByWeight = this.diarySummary.groupedProductsByWeight.sort(function (a, b) {
                    return a.weight < b.weight ? 1 : a.weight > b.weight ? -1 : 0;
                });

                this.diarySummary.recipesByDate = this.diarySummary.recipesByDate.sort((a, b) => {
                    const aDate = moment(a.date);
                    const bDate = moment(b.date);

                    return aDate > bDate ? 1 : aDate < bDate ? -1 : 0;
                });

                this.diarySummary.recipesByDate = this.diarySummary.recipesByDate.map((item) => {
                    item.date = moment(item.date).format('DD.MM.YYYY');

                    return item;
                });

                this.changeDetectorRef.markForCheck();
                this.appLoadingEventsEmitter.broadcastAppIsLoading(false);
            }, () => {
                this.appLoadingEventsEmitter.broadcastAppIsLoading(false);
                alert('Грешка със сървъра. Моля опитайте по-късно.');
            });
    }

    resetDates() {
        this.fromDate = this.getMonday(new Date());
        this.toDate = new Date((new Date()).setDate((new Date()).getDate() - (new Date()).getDay() + (((new Date()).getDay() === 0 ? -6 : 1)) + 6));
    }

    onStartDateChange(event) {
        const fromDate = moment(this.fromDate);
        const toDate = moment(this.toDate);

        if (fromDate > toDate) {
            this.toDate = this.fromDate;
            this.changeDetectorRef.detectChanges();
        }

        this.init();
    }

    onToDateChange(event) {
        const fromDate = moment(this.fromDate);
        const toDate = moment(this.toDate);

        if (fromDate > toDate) {
            this.fromDate = this.toDate;
            this.changeDetectorRef.detectChanges();
        }

        this.init();
    }

    numberWithSpaces(x) {
        return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    }

    numberWithSpacesDecimal(x) {
        const parts = x.toString().split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        return parts.join('.');
    }

    private getMonday(date) {
        return new Date(date.setDate((date.getDate() - date.getDay() + (date.getDay() === 0 ? -6 : 1))));
    }

    numberKg(weight: number) {
        weight = Math.round(weight);

        if (weight < 1000) {
            return this.numberWithSpaces(weight) + ' гр.';
        }

        return this.numberWithSpacesDecimal(weight / 1000) + ' кг.';
    }

    onChangeClients() {
        this.masterService.updateDiarySummaryClients(
            this.usersService.userProfile.id,
            moment(this.fromDate).format('YYYY-MM-DD'),
            moment(this.toDate).format('YYYY-MM-DD'),
            this.selectedClientIds
        ).subscribe(() => {
            this.initStatistics();
        });
    }

    openRecipeProducts(recipe: MasterRecipeViewModel) {
        this.viewRecipeBsModalRef = this.bsModalService.show(ViewRecipeStatisticsComponent, {
            backdrop: true
        });

        this.viewRecipeBsModalRef.content.bsModaRefStatistics = this.bsModalRef;
        this.viewRecipeBsModalRef.content.recipe = recipe;

        this.bsModalEventsEmitter.broadcastBsModalStateChanged(this.viewRecipeBsModalRef);
    }

    groupRecipesByMeal(recipes: MasterRecipeViewModel[]) {
        return recipes.sort((a, b) => {
            return a.meal.id - b.meal.id;
        });
    }
}
